/* =============================================
   ATHLYST Scroll Animation Engine
   Drives the entire homepage scroll experience:
   - Intersection Observer for scroll reveals
   - Smooth parallax for images
   - Scroll progress bar
   - Cursor glow tracking
   - Staggered children animations
   - Section-specific auto-discovery
   ============================================= */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----- SCROLL REVEAL ENGINE ----- */
  function initScrollReveal() {
    var revealEls = document.querySelectorAll('[data-scroll-reveal]');
    if (!revealEls.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var delay = parseInt(el.dataset.scrollDelay || '0', 10);
          setTimeout(function () {
            el.classList.add('is-scrolled-in');
          }, delay);
          observer.unobserve(el);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -60px 0px'
    });

    revealEls.forEach(function (el) { observer.observe(el); });
  }

  /* ----- AUTO-DISCOVER SECTIONS ----- */
  function autoDiscoverSections() {
    // Target section wrappers on the homepage
    var sections = document.querySelectorAll('.template-index .shopify-section');
    if (!sections.length) return;

    var sectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-in-view');
          sectionObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -30px 0px'
    });

    sections.forEach(function (s) { sectionObserver.observe(s); });

    // Auto-reveal title wrappers
    var titles = document.querySelectorAll('.template-index .title-wrapper-with-link');
    titles.forEach(function (t) {
      if (!t.hasAttribute('data-scroll-reveal')) {
        t.setAttribute('data-scroll-reveal', 'fade-up');
      }
    });

    // Auto-reveal support block cards
    var supportCards = document.querySelectorAll('.support-block .support-block-list__item');
    supportCards.forEach(function (card, i) {
      if (!card.hasAttribute('data-scroll-reveal')) {
        card.setAttribute('data-scroll-reveal', 'fade-up');
        card.setAttribute('data-scroll-delay', String(i * 120));
      }
    });

    // Auto-reveal hotspot image and points
    var hotspotImage = document.querySelector('.hotspot-block-section .dt-sc-hotspot-image');
    if (hotspotImage && !hotspotImage.hasAttribute('data-scroll-reveal')) {
      hotspotImage.setAttribute('data-scroll-reveal', 'scale-up');
    }
    var hotspotPoints = document.querySelectorAll('.hotspot-block-section .dt-sc-hotspot-item');
    hotspotPoints.forEach(function (pt, i) {
      if (!pt.hasAttribute('data-scroll-reveal')) {
        pt.setAttribute('data-scroll-reveal', 'pop-in');
        pt.setAttribute('data-scroll-delay', String(400 + i * 200));
      }
    });

    // Auto-reveal collapsible/FAQ accordion rows
    var faqRows = document.querySelectorAll('.collapsible-content .accordion details');
    faqRows.forEach(function (row, i) {
      if (!row.hasAttribute('data-scroll-reveal')) {
        row.setAttribute('data-scroll-reveal', 'slide-up');
        row.setAttribute('data-scroll-delay', String(i * 100));
      }
    });

    // Auto-reveal collapsible/FAQ image
    var faqImage = document.querySelector('.collapsible-content .collapsible-content__media img');
    if (faqImage && !faqImage.hasAttribute('data-scroll-reveal')) {
      faqImage.setAttribute('data-scroll-reveal', 'scale-up');
    }

    // Auto-reveal image-with-banner tab items
    var bannerItems = document.querySelectorAll('.image-with-banner .image-with-banner-wrapper');
    bannerItems.forEach(function (item, i) {
      if (!item.hasAttribute('data-scroll-reveal')) {
        item.setAttribute('data-scroll-reveal', 'fade-up');
        item.setAttribute('data-scroll-delay', String(i * 150));
      }
    });

    // Auto-reveal marquee section
    var marquee = document.querySelector('.marquee-section');
    if (marquee && !marquee.hasAttribute('data-scroll-reveal')) {
      marquee.setAttribute('data-scroll-reveal', 'fade-in');
    }
  }

  /* ----- SMOOTH PARALLAX ----- */
  function initParallax() {
    var parallaxEls = document.querySelectorAll('[data-scroll-parallax]');
    // Also auto-add parallax to certain elements
    var hotspotImg = document.querySelector('.hotspot-block-section .dt-sc-hotspot');
    if (hotspotImg && !hotspotImg.hasAttribute('data-scroll-parallax')) {
      hotspotImg.setAttribute('data-scroll-parallax', '0.03');
    }
    var faqMedia = document.querySelector('.collapsible-content .collapsible-content__media');
    if (faqMedia && !faqMedia.hasAttribute('data-scroll-parallax')) {
      faqMedia.setAttribute('data-scroll-parallax', '0.04');
    }

    // Re-query after auto-adding
    parallaxEls = document.querySelectorAll('[data-scroll-parallax]');
    if (!parallaxEls.length) return;

    var ticking = false;

    function updateParallax() {
      var vh = window.innerHeight;
      parallaxEls.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var center = rect.top + rect.height / 2;
        var progress = (center - vh / 2) / vh;
        var speed = parseFloat(el.dataset.scrollParallax) || 0.05;
        var shift = progress * vh * speed * -1;
        el.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });

    updateParallax();
  }

  /* ----- SCROLL PROGRESS BAR ----- */
  function initScrollProgress() {
    var bar = document.querySelector('.scroll-progress-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'scroll-progress-bar';
      document.body.appendChild(bar);
    }

    var ticking = false;
    function update() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      var pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      bar.style.width = pct + '%';
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* ----- CURSOR GLOW ----- */
  function initCursorGlow() {
    if (window.innerWidth < 990) return;

    var glow = document.querySelector('.cursor-glow');
    if (!glow) {
      glow = document.createElement('div');
      glow.className = 'cursor-glow';
      document.body.appendChild(glow);
    }

    var mouseX = 0, mouseY = 0;
    var glowX = 0, glowY = 0;
    var animating = false;

    document.addEventListener('mousemove', function (e) {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (!animating) {
        animating = true;
        smoothGlow();
      }
    }, { passive: true });

    function smoothGlow() {
      glowX += (mouseX - glowX) * 0.12;
      glowY += (mouseY - glowY) * 0.12;
      glow.style.left = glowX + 'px';
      glow.style.top = glowY + 'px';

      if (Math.abs(mouseX - glowX) > 0.5 || Math.abs(mouseY - glowY) > 0.5) {
        requestAnimationFrame(smoothGlow);
      } else {
        animating = false;
      }
    }
  }

  /* ----- HERO SCROLL-AWAY EFFECT ----- */
  function initHeroScrollAway() {
    var hero = document.querySelector('.template-index .slideshow');
    if (!hero) return;

    var slideContent = hero.querySelectorAll('.slideshow__text-wrapper');
    var ticking = false;

    function update() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      var heroHeight = hero.offsetHeight;

      if (scrollTop < heroHeight) {
        var progress = scrollTop / heroHeight;
        var opacity = 1 - progress * 1.5;
        var translateY = scrollTop * 0.3;
        var scale = 1 + progress * 0.05;

        slideContent.forEach(function (el) {
          el.style.opacity = Math.max(0, opacity);
          el.style.transform = 'translate3d(0,' + translateY + 'px,0)';
        });

        // Subtle zoom on hero images
        var imgs = hero.querySelectorAll('.slideshow__media img');
        imgs.forEach(function (img) {
          img.style.transform = 'scale(' + scale.toFixed(3) + ')';
        });
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ----- HERO SCROLL INDICATOR ----- */
  function initHeroScrollIndicator() {
    var hero = document.querySelector('.template-index .slideshow');
    if (!hero) return;

    // Check if already has one
    if (hero.querySelector('.hero-scroll-indicator')) return;

    var indicator = document.createElement('div');
    indicator.className = 'hero-scroll-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    indicator.innerHTML = '<span class="hero-scroll-indicator__text">Scroll</span>' +
      '<span class="hero-scroll-indicator__line"></span>';
    hero.style.position = 'relative';
    hero.appendChild(indicator);

    // Fade out on scroll
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          indicator.style.opacity = Math.max(0, 1 - scrollTop / 200);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ----- MARQUEE SCROLL SPEED BOOST ----- */
  function initMarqueeScrollBoost() {
    var marquee = document.querySelector('.marquee-section .marquee-list');
    if (!marquee) return;

    var baseSpeed = 1;
    var ticking = false;

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var rect = marquee.getBoundingClientRect();
          var vh = window.innerHeight;
          if (rect.top < vh && rect.bottom > 0) {
            var center = (rect.top + rect.height / 2 - vh / 2) / vh;
            var boost = 1 + Math.abs(center) * 0.6;
            marquee.style.animationDuration = (30 / boost) + 's';
            var sibling = marquee.nextElementSibling;
            if (sibling && sibling.classList.contains('marquee-list')) {
              sibling.style.animationDuration = (30 / boost) + 's';
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ----- IMAGE-WITH-BANNER PARALLAX BG ----- */
  function initBannerImageParallax() {
    var bannerSection = document.querySelector('.image-with-banner .image-with-banner-grid');
    if (!bannerSection) return;

    var images = bannerSection.querySelectorAll('.image-banner-media img');
    var ticking = false;

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var rect = bannerSection.getBoundingClientRect();
          var vh = window.innerHeight;
          if (rect.top < vh && rect.bottom > 0) {
            var progress = (rect.top) / vh;
            images.forEach(function (img) {
              var shift = progress * 40;
              img.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0) scale(1.05)';
            });
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ----- COUNTER ANIMATION FOR STATS ----- */
function initCounterAnimation() {
  var counters = document.querySelectorAll('[data-scroll-counter]');
  if (!counters.length) return;

  counters.forEach(function (el) {
    var target = parseInt(el.dataset.scrollCounter, 10);

    // 👇 Show instantly (prevents blank)
    if (!el.textContent.trim()) {
      el.textContent = target;
    }

    var duration = 400;
    var startTime = null;

    function step(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.floor(eased * target);
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  });
}

// 👇 Run immediately when page loads
document.addEventListener('DOMContentLoaded', function () {
  initCounterAnimation();
});

  /* ----- SMOOTH SCROLL ----- */
  function initSmoothScroll() {
    document.documentElement.style.scrollBehavior = 'smooth';
  }

  /* ----- INITIALIZE ----- */
  function init() {
    if (prefersReducedMotion) {
      // Just add scroll progress and smooth scroll
      initScrollProgress();
      return;
    }

    autoDiscoverSections();
    initScrollReveal();
    initParallax();
    initScrollProgress();
    initCursorGlow();
    initHeroScrollAway();
    initHeroScrollIndicator();
    initMarqueeScrollBoost();
    initBannerImageParallax();
    initCounterAnimation();
    initSmoothScroll();
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
